import { useEffect, useMemo, useState } from 'react';
import {
  getMetrics,
  getRevenue,
  putMrr,
  putPepm,
  type MetricPoint,
  type MrrClient,
  type MrrSettings,
  type PepmSettings,
} from '../api.js';
import Sparkline from '../components/Sparkline.js';
import { Button, Card, ErrorState, Group, Icon, IconButton, MoreRow, Row, Sheet, useToast } from '../ui/index.js';

/**
 * Income — the two streams tracked by hand rather than imported.
 *
 * PEPM is the day job: members on the platform × a per-member rate, plus a
 * full-time base. MRR is Rova Web: a list of clients and what each pays.
 * Both are saved server-side and every save stamps today's figure into the
 * metrics table, so the "running tally" is a real history (the sparklines),
 * not just the last number typed in.
 */

const CHECKPOINTS = [3_000, 5_000, 10_000, 15_000, 20_000, 50_000, 100_000, 200_000];

/** Whole-dollar formatting; cents are noise at these magnitudes. */
function usd(cents: number): string {
  return `$${Math.round(cents / 100).toLocaleString()}`;
}

/** Decimal string → cents, without going through float multiplication. */
export function toCents(s: string): number {
  const m = /^\s*(\d*)(?:\.(\d{0,2})\d*)?\s*$/.exec(s);
  if (!m) return 0;
  const whole = Number(m[1] || '0');
  const frac = Number((m[2] ?? '').padEnd(2, '0'));
  return whole * 100 + frac;
}

/** Cents → "0.50" for an input box. */
function fromCents(c: number): string {
  return (c / 100).toFixed(2).replace(/\.00$/, '');
}

/** Cents → "$0.50" / "$40" for a rate label. */
function rate(c: number): string {
  return `$${fromCents(c)}`;
}

export interface PepmRow {
  members: number;
  monthlyCents: number;
  yearlyCents: number;
  withBaseCents: number;
}

export function pepm(members: number, p: Omit<PepmSettings, 'members'>): PepmRow {
  const monthlyCents = members * p.pepmCents;
  const yearlyCents = monthlyCents * 12;
  return {
    members,
    monthlyCents,
    yearlyCents,
    withBaseCents: yearlyCents + p.hourlyCents * p.hoursPerYear,
  };
}

export function mrrTotal(m: MrrSettings): number {
  return m.clients.reduce((s, c) => s + c.cents, 0);
}

/** Last 365 days of a finance metric, daily. */
function useHistory(type: string, bump: number): MetricPoint[] {
  const [points, setPoints] = useState<MetricPoint[]>([]);
  useEffect(() => {
    const to = new Date();
    const from = new Date(to.getTime() - 365 * 86_400_000);
    void getMetrics({
      type,
      from: from.toISOString(),
      to: to.toISOString(),
      bucket: 'day',
      agg: 'max',
      source: 'finance',
    })
      .then((r) => setPoints(r.points))
      .catch(() => setPoints([]));
  }, [type, bump]);
  return points;
}

export default function Income(): JSX.Element {
  const [pepmSaved, setPepmSaved] = useState<PepmSettings | null>(null);
  const [mrrSaved, setMrrSaved] = useState<MrrSettings | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [bump, setBump] = useState(0);
  const [editing, setEditing] = useState<'pepm' | 'mrr' | null>(null);
  const toast = useToast();

  useEffect(() => {
    void getRevenue()
      .then((r) => {
        setPepmSaved(r.pepm);
        setMrrSaved(r.mrr);
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load'));
  }, []);

  const membersHistory = useHistory('pepm_members', bump);
  const mrrHistory = useHistory('mrr_usd', bump);

  if (error) return <ErrorState message={error} />;
  if (!pepmSaved || !mrrSaved) {
    return (
      <ul className="ui-group" aria-busy="true" aria-label="Loading">
        <li className="hb-skelrow" />
        <li className="hb-skelrow" />
        <li className="hb-skelrow" />
      </ul>
    );
  }

  const pepmNow = pepm(pepmSaved.members, pepmSaved);
  const mrrMonthly = mrrTotal(mrrSaved);
  const baseYearly = pepmSaved.hourlyCents * pepmSaved.hoursPerYear;

  return (
    <>
      <Card className="mn-hero" label="Monthly income">
        <div className="hb-hero">
          <div className="hb-hero__text">
            <span className="hb-hero__label">Monthly, all in</span>
            <span className="hb-hero__value">{usd(pepmNow.monthlyCents + mrrMonthly)}</span>
            <span className="hb-hero__sub">
              PEPM <b className="num">{usd(pepmNow.monthlyCents)}</b> · MRR <b className="num">{usd(mrrMonthly)}</b>
            </span>
          </div>
        </div>
        <ul className="hb-legend">
          <li>
            <span className="hb-legend__name">Yearly, with base</span>
            <span className="hb-legend__val num">{usd(pepmNow.withBaseCents + mrrMonthly * 12)}</span>
          </li>
          <li>
            <span className="hb-legend__name">Base salary</span>
            <span className="hb-legend__val num">{usd(baseYearly)}</span>
          </li>
        </ul>
      </Card>

      <PepmSummary
        saved={pepmSaved}
        mrrMonthlyCents={mrrMonthly}
        history={membersHistory}
        onEdit={() => setEditing('pepm')}
      />

      <Group header="MRR · Rova Web" action={{ label: 'Edit', onClick: () => setEditing('mrr') }}>
        {mrrSaved.clients.length === 0 ? (
          <Row
            leading={<Icon name="plus" size={20} />}
            title="Add a client"
            sub="And what they pay per month"
            tone="accent"
            onClick={() => setEditing('mrr')}
          />
        ) : (
          mrrSaved.clients.map((c, i) => (
            <Row key={`${c.name}-${i}`} title={c.name} detail={<span className="hb-amount">{usd(c.cents)}<small className="mn-per">/mo</small></span>} />
          ))
        )}
        {mrrSaved.clients.length > 0 && (
          <Row
            className="mn-total"
            title="Total"
            sub={`${usd(mrrMonthly * 12)} a year`}
            detail={<span className="hb-amount mn-total__amt">{usd(mrrMonthly)}<small className="mn-per">/mo</small></span>}
          />
        )}
        {mrrHistory.length > 1 && <SparkRow label="MRR over time" points={mrrHistory} />}
      </Group>

      {editing === 'pepm' && (
        <PepmSheet
          saved={pepmSaved}
          onClose={() => setEditing(null)}
          onSaved={(p) => {
            setPepmSaved(p);
            setBump((b) => b + 1);
            setEditing(null);
            toast.info('PEPM saved');
          }}
        />
      )}
      {editing === 'mrr' && (
        <MrrSheet
          saved={mrrSaved}
          onClose={() => setEditing(null)}
          onSaved={(m) => {
            setMrrSaved(m);
            setBump((b) => b + 1);
            setEditing(null);
            toast.info('MRR saved');
          }}
        />
      )}
    </>
  );
}

function SparkRow({ label, points }: { label: string; points: MetricPoint[] }): JSX.Element {
  return (
    <li className="ui-swipe">
      <div className="mn-spark">
        <span className="mn-spark__label">{label}</span>
        <span className="mn-spark__line">
          <Sparkline points={points} color="var(--accent)" width={320} height={36} />
        </span>
      </div>
    </li>
  );
}

// --- PEPM ------------------------------------------------------------------

function PepmSummary({
  saved,
  mrrMonthlyCents,
  history,
  onEdit,
}: {
  saved: PepmSettings;
  mrrMonthlyCents: number;
  history: MetricPoint[];
  onEdit: () => void;
}): JSX.Element {
  // Levels above where we are now: the current count first, then each fixed
  // checkpoint beyond it. Nothing below — the question is "what's next".
  const levels = useMemo(
    () => [saved.members, ...CHECKPOINTS.filter((m) => m > saved.members)],
    [saved.members],
  );
  const [allLevels, setAllLevels] = useState(false);
  const shownLevels = allLevels ? levels : levels.slice(0, 4);

  return (
    <>
      <Group header="PEPM · day job" action={{ label: 'Edit', onClick: onEdit }}>
        <Row title="Members on platform" detail={<span className="hb-amount">{saved.members.toLocaleString()}</span>} onClick={onEdit} />
        <Row title="Rate" detail={`${rate(saved.pepmCents)} / member / mo`} onClick={onEdit} />
        <Row
          title="Base"
          sub={`${rate(saved.hourlyCents)}/hr × ${saved.hoursPerYear.toLocaleString()} h`}
          detail={<span className="hb-amount">{usd(saved.hourlyCents * saved.hoursPerYear)}<small className="mn-per">/yr</small></span>}
          onClick={onEdit}
        />
        {history.length > 1 && <SparkRow label="Members over time" points={history} />}
      </Group>

      <Group header="From here up" footer="Monthly and yearly, with MRR and base included.">
        {shownLevels.map((m, idx) => {
          const r = pepm(m, saved);
          const monthlyAll = r.monthlyCents + mrrMonthlyCents;
          const yearlyAll = r.withBaseCents + mrrMonthlyCents * 12;
          return (
            <Row
              key={m}
              className={idx === 0 ? 'mn-level mn-level--now' : 'mn-level'}
              title={
                <>
                  {m.toLocaleString()} members
                  {idx === 0 && <span className="hb-pill hb-pill--accent mn-level__tag">now</span>}
                </>
              }
              sub={`${usd(monthlyAll)}/mo all in · PEPM ${usd(r.monthlyCents)}`}
              detail={<span className="hb-amount">{usd(yearlyAll)}<small className="mn-per">/yr</small></span>}
            />
          );
        })}
        {!allLevels && <MoreRow count={levels.length - shownLevels.length} noun="more levels" onClick={() => setAllLevels(true)} />}
      </Group>
    </>
  );
}

function PepmSheet({
  saved,
  onClose,
  onSaved,
}: {
  saved: PepmSettings;
  onClose: () => void;
  onSaved: (p: PepmSettings) => void;
}): JSX.Element {
  const [members, setMembers] = useState(String(saved.members));
  const [pepmRate, setPepmRate] = useState(fromCents(saved.pepmCents));
  const [hourly, setHourly] = useState(fromCents(saved.hourlyCents));
  const [hours, setHours] = useState(String(saved.hoursPerYear));
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  const draft: PepmSettings = {
    members: Math.max(0, Math.floor(Number(members) || 0)),
    pepmCents: toCents(pepmRate),
    hourlyCents: toCents(hourly),
    hoursPerYear: Math.max(0, Math.floor(Number(hours) || 0)),
  };
  const dirty =
    draft.members !== saved.members ||
    draft.pepmCents !== saved.pepmCents ||
    draft.hourlyCents !== saved.hourlyCents ||
    draft.hoursPerYear !== saved.hoursPerYear;
  const row = pepm(draft.members, draft);
  const baseCents = draft.hourlyCents * draft.hoursPerYear;

  const save = async () => {
    setBusy(true);
    setNote(null);
    try {
      onSaved(await putPepm(draft));
    } catch (e) {
      setNote(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet
      title="PEPM"
      onClose={onClose}
      action={{ label: busy ? 'Saving…' : 'Save', onClick: () => void save(), disabled: busy || !dirty }}
    >
      <form
        className="hb-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (dirty && !busy) void save();
        }}
      >
        <label className="hb-field">
          <span>Members on platform</span>
          <input inputMode="numeric" value={members} onChange={(e) => setMembers(e.target.value)} data-autofocus />
        </label>
        <label className="hb-field">
          <span>PEPM ($/member/mo)</span>
          <input inputMode="decimal" value={pepmRate} onChange={(e) => setPepmRate(e.target.value)} />
        </label>
        <div className="hb-form__row">
          <label className="hb-field">
            <span>Base ($/hr)</span>
            <input inputMode="decimal" value={hourly} onChange={(e) => setHourly(e.target.value)} />
          </label>
          <label className="hb-field">
            <span>Hours / yr</span>
            <input inputMode="numeric" value={hours} onChange={(e) => setHours(e.target.value)} />
          </label>
        </div>
        <ul className="hb-legend mn-figs">
          <li>
            <span className="hb-legend__name">Monthly</span>
            <span className="hb-legend__val num">{usd(row.monthlyCents)}</span>
          </li>
          <li>
            <span className="hb-legend__name">Yearly</span>
            <span className="hb-legend__val num">{usd(row.yearlyCents)}</span>
          </li>
          <li>
            <span className="hb-legend__name">Base</span>
            <span className="hb-legend__val num">{usd(baseCents)}</span>
          </li>
          <li>
            <span className="hb-legend__name">Total a year</span>
            <span className="hb-legend__val num hb-amount--in">{usd(row.withBaseCents)}</span>
          </li>
        </ul>
        {note && (
          <p className="hb-form__error" role="alert">
            {note}
          </p>
        )}
        <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
      </form>
    </Sheet>
  );
}

// --- MRR -------------------------------------------------------------------

interface ClientDraft {
  name: string;
  amount: string;
}

function MrrSheet({
  saved,
  onClose,
  onSaved,
}: {
  saved: MrrSettings;
  onClose: () => void;
  onSaved: (m: MrrSettings) => void;
}): JSX.Element {
  const toDraft = (cs: MrrClient[]): ClientDraft[] =>
    cs.map((c) => ({ name: c.name, amount: fromCents(c.cents) }));
  const [clients, setClients] = useState<ClientDraft[]>(() =>
    saved.clients.length ? toDraft(saved.clients) : [{ name: '', amount: '' }],
  );
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  const draft: MrrSettings = {
    clients: clients
      .filter((c) => c.name.trim().length > 0)
      .map((c) => ({ name: c.name.trim(), cents: toCents(c.amount) })),
  };
  const dirty = JSON.stringify(draft) !== JSON.stringify(saved);
  const total = mrrTotal(draft);

  const update = (i: number, patch: Partial<ClientDraft>) =>
    setClients((cs) => cs.map((c, j) => (j === i ? { ...c, ...patch } : c)));
  const remove = (i: number) => setClients((cs) => cs.filter((_, j) => j !== i));
  const add = () => setClients((cs) => [...cs, { name: '', amount: '' }]);

  const save = async () => {
    setBusy(true);
    setNote(null);
    try {
      onSaved(await putMrr(draft));
    } catch (e) {
      setNote(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet
      title="MRR clients"
      onClose={onClose}
      action={{ label: busy ? 'Saving…' : 'Save', onClick: () => void save(), disabled: busy || !dirty }}
    >
      <div className="hb-form">
        <ul className="mn-clients">
          {clients.map((c, i) => (
            <li key={i} className="mn-client">
              <input
                className="mn-client__name"
                value={c.name}
                placeholder="Client"
                aria-label={`Client ${i + 1} name`}
                onChange={(e) => update(i, { name: e.target.value })}
              />
              <label className="mn-client__amt">
                <span aria-hidden="true">$</span>
                <input
                  inputMode="decimal"
                  value={c.amount}
                  placeholder="0"
                  aria-label={`Client ${i + 1} dollars per month`}
                  onChange={(e) => update(i, { amount: e.target.value })}
                />
              </label>
              <IconButton icon="x" label="Remove" size={18} onClick={() => remove(i)} />
            </li>
          ))}
        </ul>
        <Button variant="secondary" icon="plus" onClick={add}>
          Add client
        </Button>
        <ul className="hb-legend mn-figs">
          <li>
            <span className="hb-legend__name">Monthly</span>
            <span className="hb-legend__val num">{usd(total)}</span>
          </li>
          <li>
            <span className="hb-legend__name">Yearly</span>
            <span className="hb-legend__val num">{usd(total * 12)}</span>
          </li>
        </ul>
        {note && (
          <p className="hb-form__error" role="alert">
            {note}
          </p>
        )}
      </div>
    </Sheet>
  );
}
